import { HttpClient, HttpHeaders } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { Injectable } from '@angular/core';
import { AppConstants } from 'src/app/shared/constants/app.contants';
import {  CountryMaster } from 'src/app/shared/models/app.models';
import { lastValueFrom, Observable, of } from 'rxjs';
import { LoaderService } from 'src/app/shared/services/loader.service';
import { UserService } from './user.service';

@Injectable({
  providedIn: 'root'
})


export class ApiService {
    //#region fields
    private _baseUrl?: string;
    //#endregion
    private readonly CACHE_VERSION = 'v2';
  
    private CACHE_EXPIRY_KEY = 'allLookupsExpiry';
    private CACHE_EXPIRY_TIME = 24 * 60 * 60 * 1000; // 24 hours in milliseconds   
    // Variables to hold lookup data
    public countries: any[] = [];
    public currencies: any[] = [];
    public methodsOfPayment: any[] = [];
    public categories: any[] = [];
    public subCategories: any[] = [];
    public termsOfPayment: any[] = [];
    public preQualifications: any[] = [];
    public banks: any[] = [];
    public documentsMaster: any[] = [];
    
    

    //#region Ctor 
    constructor(private _http: HttpClient, 
       private _loaderService?: LoaderService,
       private _userService?: UserService,
      ) {
        // Clear old cache format on service initialization
        this.clearOldCacheFormat();
    }
    //#endregion

    private get cacheKey(): string {
      return `allLookups_${this.CACHE_VERSION}_${this._userService?.user?.Id || 'anon'}`;
    }


    //#region Private actions
    private buildUrl(controllerName: string, actionName: string, withPrefix: boolean, prefix: string | null): string {
      if(withPrefix && prefix)return this._baseUrl + '/' + prefix + '/'+ controllerName + '/' + actionName
      return this._baseUrl + '/' + controllerName + '/' + actionName
    }

    // Public method to set base URL
    public setBaseUrl(url: string): void {
      this._baseUrl = url;
    }

    // Clear old cache format
    private clearOldCacheFormat(): void {
      // Check if old format exists (without versioning)
      const oldCacheKey = 'allLookups';
      if (localStorage.getItem(oldCacheKey)) {
        console.log('Clearing old cache format...');
        localStorage.removeItem(oldCacheKey);
        localStorage.removeItem('allLookupsExpiry');
      }
    }

    // Method to check if cache is valid
    private isCacheValid(): boolean {
      const expiry = localStorage.getItem(this.CACHE_EXPIRY_KEY);
      if (expiry) {
        const expiryDate = new Date(expiry);
        return new Date().getTime() < expiryDate.getTime();
      }
      return false;
    }
    //#endregion

    //#region GET
    public getByUrl(url: string, keyValueParams?: {}, options?: any) {
      let queryString = '';
      if (keyValueParams) {
        const keys = Object.keys(keyValueParams);
        keys.forEach((key, index) => {
          queryString += index === 0 ? '?' : '&';
          const value = keyValueParams[key as keyof typeof keyValueParams];
          queryString += `${encodeURIComponent(key)}=${encodeURIComponent(String(value ?? ''))}`;
        });
      }
    
      // Ensure options are initialized
      if (!options) {
        options = {};
      }
    
      // Set default headers, including the Authorization header
      if (!options.headers) {
        options.headers = new HttpHeaders({
          'Content-Type': 'application/json', // Content-Type may not be necessary for GET, but including it ensures consistency
          'Authorization': 'Bearer ' +     localStorage.getItem('QNBNToken')
          // Add your method to get the token
        });
      } else {
        options.headers = new HttpHeaders({
          ...options.headers,  // Retain existing headers if there are any
          'Authorization': 'Bearer ' +   localStorage.getItem('QNBNToken')
        });
      }
    
      // Make the GET request with the headers
      // Let the interceptor handle mock data when DISABLE_BACKEND is true
      return this._http.get(url + queryString, options);
    }
    
  
    
    public get(controllerName: string, actionName: string, withPrefix: boolean, prefix: string | null, keyValueParams?: {}, options?: any) {


        const url = this.buildUrl(controllerName, actionName, withPrefix, prefix);
        return this.getByUrl(url, keyValueParams, options);


    }
    //#endregion

    //#region POST
    public postByUrl(url: string, payload: any, keyValueParams?: {}, options?: any) {
      let queryString = '';
      if (keyValueParams) {
        const keys = Object.keys(keyValueParams);
        keys.forEach((key, index) => {
          queryString += index === 0 ? '?' : '&';
          const value = keyValueParams[key as keyof typeof keyValueParams];
          queryString += `${encodeURIComponent(key)}=${encodeURIComponent(String(value ?? ''))}`;
        });
      }

      if (!options) {
        options = {};
      }
      // Set default headers, including the Authorization header
      if (!options.headers) {
        options.headers = new HttpHeaders({
          'Content-Type': 'application/json',
          'Authorization': 'Bearer ' + localStorage.getItem('QNBNToken')
        });
      } else {
        options.headers = new HttpHeaders({
          ...options.headers,  // Retain existing headers if there are any
          'Authorization': 'Bearer ' + localStorage.getItem('QNBNToken')
        });
      }

      console.log('Final URL:', url + queryString);
      console.log('Payload:', payload);
      console.log('Options:', options);
      console.log('Headers:', options.headers.keys().map((key: string) => `${key}: ${options.headers.get(key)}`));

      // Let the interceptor handle mock data when DISABLE_BACKEND is true
      return this._http.post(url + queryString, payload, options);
    }

    public post(controllerName: string, actionName: string, withPrefix: boolean, prefix: string | null, payload: any, keyValueParams?: {}, options?: any) {
        const url = this.buildUrl(controllerName, actionName, withPrefix, prefix);
        return this.postByUrl(url, payload, keyValueParams, options);
    }

    public postMultipart(controllerName: string, actionName: string, withPrefix: boolean, prefix: string | null, payload: any, allFiles: any[] = []) {
      const url = this.buildUrl(controllerName, actionName, withPrefix, prefix);
      const formData: FormData = new FormData();
      const keys = Object.keys(payload);
      keys.forEach((key, index) => {
        formData.append(key, payload[key as keyof typeof payload]);
      });

      if (allFiles?.length > 0){
        allFiles.forEach((f,index) => {
          // if (f.File){
            formData.append(`Files[${index}].typeAttachment`, f.TypeAttachment);
            formData.append(`Files[${index}].attachmentTypeEnum`, f.AttachmentTypeEnum);
            formData.append(`Files[${index}].file`, f.File);
          // }
        });
      }

      // Prepare headers with Authorization token
      const headers = new HttpHeaders({
        'Authorization': 'Bearer ' + localStorage.getItem('QNBNToken')
        // Note: Don't set Content-Type - browser will set it automatically with boundary for multipart/form-data
      });

      // formData.append("IdExpiryDate", payload.IdExpiryDate);
      // Let the interceptor handle mock data when DISABLE_BACKEND is true
      return this._http.post(url , formData, { headers });
    }

    public putMultipart(controllerName: string, actionName: string, withPrefix: boolean, prefix: string | null, payload: any) {
      const url = this.buildUrl(controllerName, actionName, withPrefix, prefix);
      const formData: FormData = new FormData();
      const keys = Object.keys(payload);
      keys.forEach((key, index) => {
        formData.append(key, payload[key as keyof typeof payload]);
      });
      
      // Prepare headers with Authorization token
      const headers = new HttpHeaders({
        'Authorization': 'Bearer ' + localStorage.getItem('QNBNToken')
        // Note: Don't set Content-Type - browser will set it automatically with boundary for multipart/form-data
      });
      
      // formData.append("IdExpiryDate", payload.IdExpiryDate);
      // Let the interceptor handle mock data when DISABLE_BACKEND is true
      return this._http.put(url , formData, { headers });
    }

    /**
     * Post multipart/form-data request by URL with files (for meetings, agenda items, etc.)
     * @param url Full URL to post to
     * @param payload Object containing form fields
     * @param files Array of File objects to upload
     * @param options Optional HTTP options
     */
    public postMultipartByUrl(url: string, payload: any, files: File[] = [], options?: any): Observable<any> {
      const formData: FormData = new FormData();
      
      // Append all payload fields to FormData
      Object.keys(payload).forEach((key: string) => {
        const value = payload[key];
        if (value !== null && value !== undefined) {
          if (Array.isArray(value)) {
            // Handle arrays (like memberIds, agendaItemIds, prIdentifiers)
            formData.append(key, JSON.stringify(value));
          } else {
            formData.append(key, value.toString());
          }
        }
      });

      // Append files to FormData in the format expected by backend
      files.forEach((file, index) => {
        formData.append(`Files[${index}].file`, file);
        formData.append(`Files[${index}].typeAttachment`, payload.processName || 'Meeting');
        formData.append(`Files[${index}].attachmentTypeEnum`, payload.processName || 'Meeting');
      });

      // Prepare headers with Authorization token
      if (!options) {
        options = {};
      }
      
      const token = 'Bearer ' + localStorage.getItem('QNBNToken');
      
      if (!options.headers) {
        options.headers = new HttpHeaders({
          'Authorization': token
          // Note: Don't set Content-Type - browser will set it automatically with boundary for multipart/form-data
        });
      } else {
        // Convert existing headers to plain object, then add Authorization
        const existingHeaders: { [key: string]: string } = {};
        if (options.headers instanceof HttpHeaders) {
          options.headers.keys().forEach((key: string) => {
            const value = options.headers.get(key);
            if (value) {
              existingHeaders[key] = value;
            }
          });
        }
        options.headers = new HttpHeaders({
          ...existingHeaders,
          'Authorization': token
        });
      }

      return this._http.post(url, formData, options);
    }

    public postMultipartByUrlByJSON(url: string, payload: any, files: File[] = [], options?: any): Observable<any> {
      const formData: FormData = new FormData();
      // Send the entire payload as a JSON string under 'payload' key
      formData.append('payload', JSON.stringify(payload));

      // Append files to FormData in the format expected by backend
      files.forEach((file, index) => {
        formData.append(`Files[${index}].file`, file);
        formData.append(`Files[${index}].typeAttachment`, payload.processName || 'Meeting');
        formData.append(`Files[${index}].attachmentTypeEnum`, payload.processName || 'Meeting');
      });

      // Prepare headers with Authorization token
      if (!options) {
        options = {};
      }
      
      const token = 'Bearer ' + localStorage.getItem('QNBNToken');
      
      if (!options.headers) {
        options.headers = new HttpHeaders({
          'Authorization': token
          // Note: Don't set Content-Type - browser will set it automatically with boundary for multipart/form-data
        });
      } else {
        // Convert existing headers to plain object, then add Authorization
        const existingHeaders: { [key: string]: string } = {};
        if (options.headers instanceof HttpHeaders) {
          options.headers.keys().forEach((key: string) => {
            const value = options.headers.get(key);
            if (value) {
              existingHeaders[key] = value;
            }
          });
        }
        options.headers = new HttpHeaders({
          ...existingHeaders,
          'Authorization': token
        });
      }

      return this._http.post(url, formData, options);
    }

    /**
     * Put multipart/form-data request by URL with files (for meetings, agenda items, etc.)
     * @param url Full URL to put to
     * @param payload Object containing form fields
     * @param files Array of File objects to upload
     * @param options Optional HTTP options
     */
    public putMultipartByUrl(url: string, payload: any, files: File[] = [], options?: any): Observable<any> {
      const formData: FormData = new FormData();
      
      // Append all payload fields to FormData
      Object.keys(payload).forEach((key: string) => {
        const value = payload[key];
        if (value !== null && value !== undefined) {
          if (Array.isArray(value)) {
            // Handle arrays (like memberIds, agendaItemIds, prIdentifiers)
            formData.append(key, JSON.stringify(value));
          } else {
            formData.append(key, value.toString());
          }
        }
      });

      // Append files to FormData in the format expected by backend
      files.forEach((file, index) => {
        formData.append(`Files[${index}].file`, file);
        formData.append(`Files[${index}].typeAttachment`, payload.processName || 'Meeting');
        formData.append(`Files[${index}].attachmentTypeEnum`, payload.processName || 'Meeting');
      });

      // Prepare headers with Authorization token
      if (!options) {
        options = {};
      }
      
      const token = 'Bearer ' + localStorage.getItem('QNBNToken');
      
      if (!options.headers) {
        options.headers = new HttpHeaders({
          'Authorization': token
          // Note: Don't set Content-Type - browser will set it automatically with boundary for multipart/form-data
        });
      } else {
        // Convert existing headers to plain object, then add Authorization
        const existingHeaders: { [key: string]: string } = {};
        if (options.headers instanceof HttpHeaders) {
          options.headers.keys().forEach((key: string) => {
            const value = options.headers.get(key);
            if (value) {
              existingHeaders[key] = value;
            }
          });
        }
        options.headers = new HttpHeaders({
          ...existingHeaders,
          'Authorization': token
        });
      }

      return this._http.put(url, formData, options);
    }

    // public uploadFiles(allFiles: FileRequest[]) {
    //   const formData = new FormData();
    //  // formData.append('Files', allFiles);
    //  allFiles.forEach((f,index) => {
    //   formData.append(`Files[${index}].typeAttachment`, f.TypeAttachment);
    //   formData.append(`Files[${index}].file`, f.File);
    //  });
    //   // formData.append("Files[2].typeAttachment", "tt1");
    //   // formData.append("Files[2].file", allFiles[0].File);
    //   // formData.append("Files[3].typeAttachment", "tt2");
    //   // formData.append("Files[3].file", allFiles[1].File);
    //   return this._http.post<any>('https://localhost:7237/api/Request/SubmitLicenceRequest', formData);
    // }
    //#endregion

    //#region PUT
    public putByUrl(url: string, payload: any, keyValueParams?: {}, options?: any) {
        let queryString = '';
        if(keyValueParams) {
          const keys = Object.keys(keyValueParams);
          keys.forEach((key, index) => {
            if(index === 0) {
              queryString += '?' + key + '=' + keyValueParams[key as keyof typeof keyValueParams];
            } else {
              queryString += '&' + key + '=' + keyValueParams[key as keyof typeof keyValueParams];
            }
          });
        }
        
        // Ensure options are initialized
        if (!options) {
          options = {};
        }
        
        // Set default headers, including the Authorization header
        if (!options.headers) {
          options.headers = new HttpHeaders({
            'Content-Type': 'application/json',
            'Authorization': 'Bearer ' + localStorage.getItem('QNBNToken')
          });
        } else {
          options.headers = new HttpHeaders({
            ...options.headers,  // Retain existing headers if there are any
            'Authorization': 'Bearer ' + localStorage.getItem('QNBNToken')
          });
        }
        
        // Let the interceptor handle mock data when DISABLE_BACKEND is true
        return this._http.put(url + queryString, payload, options);
    }

    public put(controllerName: string, actionName: string, withPrefix: boolean, prefix: string | null, payload: any, keyValueParams?: {}, options?: any) {
        const url = this.buildUrl(controllerName, actionName, withPrefix, prefix);
        return this.putByUrl(url, payload, keyValueParams, options);
    }
    //#endregion

    //#region PATCH
    public pacthByUrl(url: string, payload: any, keyValueParams?: {}, options?: any) {
        let queryString = '';
        if(keyValueParams) {
          const keys = Object.keys(keyValueParams);
          keys.forEach((key, index) => {
            if(index === 0) {
              queryString += '?' + key + '=' + keyValueParams[key as keyof typeof keyValueParams];
            } else {
              queryString += '&' + key + '=' + keyValueParams[key as keyof typeof keyValueParams];
            }
          });
        }
        
        // Ensure options are initialized
        if (!options) {
          options = {};
        }
        
        // Set default headers, including the Authorization header
        if (!options.headers) {
          options.headers = new HttpHeaders({
            'Content-Type': 'application/json',
            'Authorization': 'Bearer ' + localStorage.getItem('QNBNToken')
          });
        } else {
          options.headers = new HttpHeaders({
            ...options.headers,  // Retain existing headers if there are any
            'Authorization': 'Bearer ' + localStorage.getItem('QNBNToken')
          });
        }
        
        // Let the interceptor handle mock data when DISABLE_BACKEND is true
        return this._http.patch(url + queryString, payload, options);
    }

    public patch(controllerName: string, actionName: string, withPrefix: boolean, prefix: string | null, payload: any, keyValueParams?: {}, options?: any) {
        const url = this.buildUrl(controllerName, actionName, withPrefix, prefix);
        return this.pacthByUrl(url, payload, keyValueParams, options);
    }
    //#endregion

    //#region DELETE
    public deleteByUrl(url: string, keyValueParams: any, options: any) {
        let queryString = '';
        if(keyValueParams) {
          const keys = Object.keys(keyValueParams);
          keys.forEach((key, index) => {
            if(index === 0) {
              queryString += '?' + key + '=' + keyValueParams[key as keyof typeof keyValueParams];
            } else {
              queryString += '&' + key + '=' + keyValueParams[key as keyof typeof keyValueParams];
            }
          });
        }
        
        // Ensure options are initialized
        if (!options) {
          options = {};
        }
        
        // Set default headers, including the Authorization header
        if (!options.headers) {
          options.headers = new HttpHeaders({
            'Authorization': 'Bearer ' + localStorage.getItem('QNBNToken')
          });
        } else {
          options.headers = new HttpHeaders({
            ...options.headers,  // Retain existing headers if there are any
            'Authorization': 'Bearer ' + localStorage.getItem('QNBNToken')
          });
        }
        
        // Let the interceptor handle mock data when DISABLE_BACKEND is true
        return this._http.delete(url + queryString, options);
    }
    public delete(controllerName: string, actionName: string, withPrefix: boolean, prefix: string | null,  keyValueParams?: {}, options?: any) {
        const url = this.buildUrl(controllerName, actionName, withPrefix, prefix);
        return this.deleteByUrl(url, keyValueParams, options);
    }
    //#endregion



    //#region lookups

  




     // Method to fetch lookups from the backend
  private async fetchLookupsFromBackend(): Promise<any> {
    this.setBaseUrl(environment.API_URL);
    const url = this.buildUrl(AppConstants.CONTROLLERS.Lookups, 'all-lookups', true, environment.APIs_PREFIX);
    let req$ = this.getByUrl(url);
    const data = await lastValueFrom(req$);
    return data;
  }


  // Main method to load all lookups (from cache or backend)
  public async loadAllLookups(): Promise<void> {
    if (this.isCacheValid()) {
      // Use cached data
      const cachedData = JSON.parse(localStorage.getItem(this.cacheKey)!);
      this.assignLookups(cachedData);
    } else {
      // Fetch fresh data from backend
      const data = await this.fetchLookupsFromBackend();
      localStorage.setItem(this.cacheKey, JSON.stringify(data));
      localStorage.setItem(this.CACHE_EXPIRY_KEY, new Date(Date.now() + this.CACHE_EXPIRY_TIME).toISOString());
      this.assignLookups(data);
    }
  }

  // Method to assign lookups to variables
  private assignLookups(data: any): void {
    this.countries = data.countries;
    this.currencies = data.currencies;
    this.methodsOfPayment = data.methodsOfPayment;
    this.categories = data.categories;
    this.subCategories = data.subCategories;
    this.termsOfPayment = data.termsOfPayment;
    this.preQualifications = data.preQualifications;
    this.banks = data.banks;
    this.documentsMaster = data.documentsMaster;

    // Deduplicate documentMaster on key properties
    this.documentsMaster = (data.documentsMaster || []).filter((doc: any, index: number, self: any[]) =>
      index === self.findIndex((d: any) =>
        d.documentName === doc.documentName &&
        d.parmDocumentFor === doc.parmDocumentFor &&
        d.processName === doc.processName
      )
    );
  }




    //#endregion



    //#region Meeting-specific methods
    
    /**
     * Save meeting data (TC Decisions, attendances, responses)
     * @param meetingId The numeric meeting ID
     * @param meetingData The meeting data to save
     * @returns Observable of the save result
     */
    public saveMeetingData(meetingId: number, meetingData: any): Observable<any> {
      this.setBaseUrl(environment.API_URL);
      const url = `${this._baseUrl}/${environment.APIs_PREFIX}/Meetings/${meetingId}/save-data`;
      return this.postByUrl(url, meetingData);
    }

    //#endregion
}
